<template>
  <div>
    <!-- 凡例 -->
    <div class="d-flex align-center mb-3 gap-3">
      <div class="d-flex align-center">
        <span
          class="legend-dot"
          style="background: #9e9e9e"
        />
        <span class="text-caption">仮予約</span>
      </div>
      <div class="d-flex align-center">
        <span
          class="legend-dot"
          style="background: #1976d2"
        />
        <span class="text-caption">確定</span>
      </div>
      <div class="d-flex align-center">
        <span
          class="legend-dot"
          style="background: #4caf50"
        />
        <span class="text-caption">来店中</span>
      </div>
      <div class="d-flex align-center">
        <span
          class="legend-dot"
          style="background: #e0e0e0"
        />
        <span class="text-caption">完了</span>
      </div>
    </div>

    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <template v-else>
      <v-tabs
        v-model="activeTab"
        class="mb-3"
      >
        <v-tab>テーブル指定</v-tab>
        <v-tab>
          テーブル未指定
          <span
            v-if="unassignedReservations.length"
            class="ml-1"
          >({{ unassignedReservations.length }})</span>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- テーブル指定: タイムライングリッド -->
        <v-tab-item>
          <timeline-grid
            :key="`timeline-${fetchKey}`"
            :reservations="reservations"
            :halls="halls"
            :show-unassigned="false"
            :check-in-enabled="isBusinessToday"
            @cell-click="onCellClick"
            @reservation-click="onReservationClick"
            @check-in="checkIn"
          />
        </v-tab-item>

        <!-- テーブル未指定: 横並びカード -->
        <v-tab-item>
          <div
            v-if="unassignedReservations.length === 0"
            class="text-body-2 text-medium-emphasis pa-4"
          >
            テーブル未指定の予約はありません
          </div>
          <div
            v-else
            class="d-flex flex-wrap gap-3 pt-2"
          >
            <v-card
              v-for="res in unassignedReservations"
              :key="`unassigned-${res.id}`"
              outlined
              class="unassigned-card"
              @click="onReservationClick(res)"
            >
              <div class="d-flex align-center mb-1">
                <span
                  class="legend-dot"
                  :style="{ background: statusColor(res) }"
                />
                <span class="text-caption">{{ statusLabel(res) }}</span>
              </div>
              <div class="text-subtitle-2 text-truncate">
                {{ customerName(res) }}
              </div>
              <div class="text-caption text--secondary">
                {{ timeRange(res) }} ・ {{ res.attributes.customerCount }}名
              </div>
              <div
                v-if="nominationSummary(res)"
                class="text-caption text--secondary text-truncate"
              >
                指名: {{ nominationSummary(res) }}
              </div>
              <div
                v-if="missingItemsOf(res).length > 0"
                class="board-warn-chip mt-1"
                :title="`来店処理には${missingItemsOf(res).join('・')}の入力が必要です`"
              >
                ⚠ 要{{ missingItemsOf(res).join('・') }}
              </div>
              <v-btn
                v-if="res.attributes.status === 'confirmed' && isBusinessToday"
                x-small
                color="success"
                class="mt-2"
                :loading="checkingInId === res.id"
                @click.stop="checkIn(res)"
              >
                <v-icon
                  x-small
                  left
                >
                  {{ icons.mdiSofaSingle }}
                </v-icon>
                卓作成
              </v-btn>
            </v-card>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </template>

    <!-- 予約作成/編集ダイアログ -->
    <reservation-dialog
      v-model="dialogOpen"
      :reservation="selectedReservation"
      :halls="halls"
      :casts="casts"
      :courses="courses"
      :referrals="referrals"
      :nominations="nominationMenus"
      :customer-tags="customerTags"
      :default-date="date"
      :default-start-time="defaultStartTime"
      :default-end-time="defaultEndTime"
      :default-hall-id="defaultHallId"
      :default-table-number="defaultTableNumber"
      @saved="onSaved"
      @checked-in="goToHalls"
      @error="onError"
    />

    <!-- エラーSnackbar -->
    <v-snackbar
      v-model="snackbar"
      color="error"
      timeout="4000"
    >
      {{ snackbarMessage }}
    </v-snackbar>

    <!-- 成功Snackbar -->
    <v-snackbar
      v-model="successSnackbar"
      color="success"
      timeout="2000"
    >
      {{ successMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { mdiSofaSingle } from '@mdi/js'
import ReservationApi from '@/api/waiter/Reservation'
import { checkInMissingItems } from '@/views/waiter/reservations/reservationDisplayUtils'
import { getBusinessTodayStr } from '@/views/waiter/floorUtils'
import useCurrentData from '@/views/composable/useCurrentData'
import HallApi from '@/api/waiter/Hall'
import UserApi from '@/api/waiter/User'
import VendibleApi from '@/api/waiter/Vendible'
import ReferralApi from '@/api/waiter/Referral'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import TimelineGrid from './TimelineGrid.vue'
import ReservationDialog from './ReservationDialog.vue'

// 予約管理の中身（凡例・卓×時間グリッド・未指定カード・編集ダイアログ）。
// 予約管理ページと営業予定の日詳細から date を渡して埋め込む
export default {
  name: 'ReservationDayBoard',
  components: {
    TimelineGrid,
    ReservationDialog,
  },
  props: {
    // 対象日（yyyy-MM-dd）
    date: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      icons: { mdiSofaSingle },
      reservations: [],
      halls: [],
      casts: [],
      courses: [],
      nominationMenus: [],
      referrals: [],
      customerTags: [],
      isLoading: false,
      fetchKey: 0,
      activeTab: 0,
      dialogOpen: false,
      selectedReservation: null,
      defaultStartTime: null,
      defaultEndTime: null,
      defaultHallId: null,
      defaultTableNumber: null,
      snackbar: false,
      snackbarMessage: '',
      successSnackbar: false,
      successMessage: '保存しました',
      checkingInId: null,
    }
  },
  computed: {
    unassignedReservations() {
      return this.reservations.filter(r => !r.attributes?.tableNumber)
    },
    // 来店処理（卓作成）は予約日=当営業日のときだけ（サーバ側でも同じ検証あり）
    isBusinessToday() {
      const { currentClub } = useCurrentData()

      return this.date === getBusinessTodayStr(new Date(), currentClub.value?.openAt)
    },
  },
  watch: {
    date() {
      this.fetchReservations()
    },
  },
  created() {
    this.fetchAll()
  },
  methods: {
    // 指名の表示（新形式の複数指名を優先、旧nominatedUserはフォールバック）
    nominationSummary(res) {
      const nominations = res.attributes.nominations || []
      if (nominations.length > 0) {
        return nominations.map(n => `${n.userName}(${n.nominationName})`).join('・')
      }

      return res.attributes.nominatedUser?.name || null
    },
    async fetchAll() {
      this.isLoading = true
      try {
        await Promise.all([
          this.fetchReservations(),
          this.fetchMasterData(),
        ])
      } finally {
        this.isLoading = false
      }
    },
    async fetchReservations() {
      try {
        const { data } = await ReservationApi.getReservations({ date: this.date })
        this.reservations = data?.reservations?.data || data?.data || []
        this.fetchKey += 1
      } catch (e) {
        console.error('[Reservation] fetch failed:', e)
      }
    },
    async fetchMasterData() {
      try {
        const [hallsRes, usersRes, vendiblesRes, referralsRes, tagsRes] = await Promise.all([
          HallApi.getHalls({ isOperation: true }),
          UserApi.getUsers(),
          VendibleApi.getVendibles(),
          ReferralApi.getReferrals(),
          CustomerTagApi.getCustomerTags(),
        ])

        this.halls = hallsRes.data?.halls?.data || []
        const allUsers = usersRes.data?.users?.data || []
        this.casts = allUsers.filter(u => u.attributes?.role === 'cast')
        const allVendibles = vendiblesRes.data?.vendibles?.data || []
        this.courses = allVendibles.filter(v => v.attributes?.vendibleType === 'Course')
        this.nominationMenus = allVendibles.filter(v => v.attributes?.vendibleType === 'Nomination')
        this.referrals = referralsRes.data?.referrals?.data || []
        this.customerTags = tagsRes.data?.customerTags?.data || []
      } catch {
        // マスタ取得失敗は許容
      }
    },
    onCellClick({
      hallId, tableNumber, startTime, endTime,
    }) {
      this.selectedReservation = null
      this.defaultStartTime = startTime
      this.defaultEndTime = endTime || null
      this.defaultHallId = hallId
      this.defaultTableNumber = tableNumber
      this.dialogOpen = true
    },
    onReservationClick(reservation) {
      this.selectedReservation = reservation
      this.dialogOpen = true
    },
    onSaved() {
      this.successMessage = '保存しました'
      this.successSnackbar = true
      this.fetchReservations()
      this.$emit('changed')
    },
    onError(msg) {
      this.snackbarMessage = msg
      this.snackbar = true
    },
    // 来店処理前に不足している項目（指名あり×コース未選択）
    missingItemsOf(reservation) {
      if (!['tentative', 'confirmed'].includes(reservation.attributes?.status)) return []

      return checkInMissingItems(reservation.attributes || {})
    },
    async checkIn(reservation) {
      if (!this.isBusinessToday) {
        this.snackbarMessage = '来店処理は予約日（当営業日）にのみ実行できます'
        this.snackbar = true

        return
      }

      // 必須項目が不足している場合はブロックし、編集ダイアログを開いて入力に誘導する
      const missing = this.missingItemsOf(reservation)
      if (missing.length > 0) {
        this.snackbarMessage = `${missing.join('・')}が未入力のため来店処理できません。予約を開いて入力してください`
        this.snackbar = true
        this.onReservationClick(reservation)

        return
      }

      // eslint-disable-next-line no-alert
      if (!window.confirm('この予約から卓を作成しますか？')) return

      this.checkingInId = reservation.id
      try {
        await ReservationApi.checkInReservation(reservation.id)
        this.$emit('changed')
        this.goToHalls()
      } catch (e) {
        this.snackbarMessage = e.response?.data?.errors?.join(', ') || '卓作成に失敗しました'
        this.snackbar = true
      } finally {
        this.checkingInId = null
      }
    },
    goToHalls() {
      // 卓作成後はホール(卓一覧)へ遷移
      this.$router.push({
        name: 'waiter-halls',
        params: { club_slug: this.$route.params.club_slug },
      })
    },
    customerName(res) {
      const customers = res.attributes?.customers
      if (!customers || customers.length === 0) return '(未設定)'

      return customers[0].name + (customers.length > 1 ? ` 他${customers.length - 1}名` : '')
    },
    timeRange(res) {
      const { startTime, endTime } = res.attributes || {}
      const fmt = t => (t ? t.slice(0, 5) : '')

      return endTime ? `${fmt(startTime)}〜${fmt(endTime)}` : fmt(startTime)
    },
    statusColor(res) {
      return {
        tentative: '#9e9e9e',
        confirmed: '#1976d2',
        checked_in: '#4caf50',
        completed: '#e0e0e0',
      }[res.attributes?.status] || '#9e9e9e'
    },
    statusLabel(res) {
      return {
        tentative: '仮予約',
        confirmed: '確定',
        checked_in: '来店中',
        completed: '完了',
        canceled: 'キャンセル',
      }[res.attributes?.status] || ''
    },
  },
}
</script>

<style scoped>
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 4px;
}

.gap-3 {
  gap: 12px;
}

.unassigned-card {
  width: 200px;
  padding: 10px 12px;
  cursor: pointer;
}

/* 来店処理の必須項目が不足している予約の警告 */
.board-warn-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  color: #b45309;
  background: rgba(232, 162, 61, 0.12);
  border: 1px solid rgba(232, 162, 61, 0.5);
  border-radius: 8px;
  padding: 0 8px;
}
</style>
